/**
 * Mappers para o componente Result List
 */

import { NAME_FIELDS, AVATAR_FIELDS } from '../shared/avatar/avatar.constants';
import { shouldHideColumn, getColumnType } from './result-list.constants';

export interface TableHeader {
    key: string;
    label: string;
    type: string;
}

/**
 * Mapeamento de labels para headers
 */
const HEADER_LABELS: { [key: string]: string } = {
    'nome': 'Nome',
    'total': 'Total',
    'valor': 'Valor',
    'data': 'Data',
    'tipo': 'Tipo',
    'descricao': 'Descrição',
    'status': 'Status',
    'telefone': 'Telefone',
    'email': 'E-mail',
    'idade': 'Idade',
    'nova_idade': 'Idade',
    'nascimento': 'Nascimento',
    'genero': 'Gênero',
    'forma_pagamento': 'Pagamento',
    'transacao': 'Transação',
    'quantidade': 'Quantidade',
    'bandeira': 'Bandeira',
    'limite': 'Limite',
    'saldo': 'Saldo',
    'gasto': 'Gasto',
    'gastos': 'Gastos'
};

/**
 * Extrai headers dos dados
 */
export function extractHeaders(data: any[]): TableHeader[] {
    if (!data || data.length === 0) return [];

    const firstItem = data[0];
    return Object.keys(firstItem).map(key => ({
        key,
        label: getHeaderLabel(key),
        type: getColumnType(key)
    }));
}

/**
 * Obtém headers para exibição na lista (exclui nome, foto e campos ocultos)
 */
export function getListDisplayHeaders(headers: TableHeader[]): TableHeader[] {
    return headers.filter(h => {
        const type = h.type;
        return type !== 'avatar' && type !== 'name' && !shouldHideColumn(h.key);
    });
}

/**
 * Obtém o label para um header
 */
export function getHeaderLabel(key: string): string {
    const lowerKey = key.toLowerCase();

    if (HEADER_LABELS[lowerKey]) {
        return HEADER_LABELS[lowerKey];
    }

    // Converte snake_case para Title Case
    return key
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
}

/**
 * Obtém o nome do membro de uma linha
 */
export function getMemberName(row: any): string {
    for (const field of NAME_FIELDS) {
        if (row[field]) {
            return row[field];
        }
    }
    return '-';
}

/**
 * Obtém a foto do membro de uma linha
 */
export function getMemberPhoto(row: any): string | null {
    for (const field of AVATAR_FIELDS) {
        if (row[field]) {
            return row[field];
        }
    }
    return null;
}

/**
 * Formata valor de célula
 */
export function formatCellValue(value: any, key: string): string {
    if (value === null || value === undefined) return '-';

    const type = getColumnType(key);

    switch (type) {
        case 'value':
            return formatCurrency(value);
        case 'date':
            return formatDate(value);
        default:
            return String(value);
    }
}

/**
 * Formata valor monetário
 */
export function formatCurrency(value: any): string {
    const numValue = typeof value === 'string' ? parseFloat(value.replace(/[^\d.-]/g, '')) : value;

    if (isNaN(numValue)) return String(value);

    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
    }).format(numValue);
}

/**
 * Formata data
 */
export function formatDate(value: any): string {
    if (!value) return '-';

    // Se já está formatado como DD/MM/YYYY
    if (typeof value === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
        return value;
    }

    // Se está no formato DDMMYYYY
    if (typeof value === 'string' && /^\d{8}$/.test(value)) {
        return `${value.slice(0, 2)}/${value.slice(2, 4)}/${value.slice(4, 8)}`;
    }

    // Tenta converter de ISO
    try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
            return date.toLocaleDateString('pt-BR');
        }
    } catch {
        // ignore
    }

    return String(value);
}

/**
 * Capitaliza texto (primeira letra maiúscula)
 */
export function capitalize(value: any): string {
    if (!value) return '-';
    const str = String(value).toLowerCase();
    return str.charAt(0).toUpperCase() + str.slice(1);
}
